import React, { FC, useState, useEffect } from 'react'

import { Table } from 'antd'
import { PaginationConfig } from 'antd/lib/pagination'
import { SortOrder, TableProps } from 'antd/lib/table'
import Column from 'antd/lib/table/Column'

import styles from './index.module.less'
import { activityService, userService } from '../../../services'
import { Activity, User, ActivityType, CauserType } from '../../../types'

type ActivityLogChange = {
	attribute: string
	current: string
	previous: string
	hasChanged: boolean
}


const ActivityPage: FC = () => {
	const [activities, setActivities] = useState<Activity[]>([])
	const [users, setUsers] = useState<User[]>([])
	const [pagination, setPagination] = useState<PaginationConfig>({})
	const [isLoading, setIsLoading] = useState<boolean>(false)

	const expandedRowRender = (activity: Activity) => {
		const data: ActivityLogChange[] = Object.keys(activity.changes?.attributes ?? activity.changes.old).map(attribute => {
			const current = activity.changes.attributes?.[attribute] || null
			const previous = activity.changes.old?.[attribute] || null
			const hasChanged = current !== previous

			return {
				attribute,
				current,
				previous,
				hasChanged
			}
		})

		const renderColumn = (text: string | boolean, record: ActivityLogChange): JSX.Element => {
			return record.hasChanged && record.previous ? <span className={styles.changedColumn}>{text?.toString()}</span> : <span>{text?.toString()}</span>
		}

		const hasPreviousData = data.some(record => record.previous)

		return (
			<Table rowKey='attribute' dataSource={data} pagination={false} size='small' style={{ padding: '1rem' }}>
				<Column
					title='Attribute'
					dataIndex='attribute'
					key='attribute'
					render={(text: string, record: ActivityLogChange) => renderColumn(text, record)}
				/>
				{hasPreviousData && (
					<Column
						title='Before'
						dataIndex='previous'
						key='previous'
						render={(text: string, record: ActivityLogChange) => renderColumn(text, record)}
					/>
				)}
				<Column
					title={hasPreviousData ? 'After' : 'Values'}
					dataIndex='current'
					key='current'
					render={(text: string, record: ActivityLogChange) => renderColumn(text, record)}
				/>
			</Table>
		)
	}

	const filterTypeMapper = {
		'end_customer:created': 'End Customer created',
		'license:created': 'License created',
		'license:updated': 'License updated',
		'reseller_license:updated': 'License limit updated',
		'reseller_report:downloaded': 'Reseller Report downloaded',
		'reseller_report:viewed': 'Reseller Report viewed',
		'service_provider_report:downloaded': 'Service Provider Report downloaded',
		'service_provider_report:viewed': 'Service Provider Report viewed',
		'subscriber:created': 'Subscriber created',
		'subscriber:deleted': 'Subscriber deleted',
		'user:updated': 'User updated',
		'user:deleted': 'User deleted'
	}

	const causerMap = {
		'CustomerPortal\\Auth\\Models\\User': 'User',
		'CustomerPortal\\ApiTokens\\ApiToken': 'API Token',
	}

	const defaultSortOrder: SortOrder = 'descend'
	const columns = [
		{
			title: 'Date',
			dataIndex: 'created_at',
			key: 'date',
			sorter: true,
			defaultSortOrder,
			render: (stringyDate: string) => {
				return new Intl.DateTimeFormat('default').format(new Date(stringyDate))
			}
		},
		{
			title: 'Type',
			dataIndex: 'type',
			key: 'type',
			filters: Object.entries(filterTypeMapper).map(([key, value]) => ({ text: value, value: key })),
			filterMultiple: false,
			render: (text: ActivityType) => filterTypeMapper[text] || text
		},
		{
			title: 'Name',
			dataIndex: 'display_name',
			key: 'name'
		},
		{
			title: 'Authentication Method',
			dataIndex: 'causer_type',
			key: 'causer_type',
			filters: [
				{ text: 'API Token', value: 'CustomerPortal\\ApiTokens\\ApiToken' },
				{ text: 'User', value: 'CustomerPortal\\Auth\\Models\\User' },
			],
			filterMultiple: false,
			render: (text: CauserType) => causerMap[text] || text
		},
		{
			title: 'Initiator',
			dataIndex: 'causer_name',
			key: 'causer',
			filters: users.map(({ name, id }) => ({ text: name, value: id.toString() })),
			filterMultiple: false,
		}
	]

	const handleTableChange: TableProps<Activity>['onChange'] = async ({ current }, filters, sorter) => {
		setPagination({ ...pagination, current })
		setIsLoading(true)

		const sortMap: { [key: string]: string } = { ascend: 'asc', descend: 'desc' }
		const { data, total, per_page } = await activityService.search(
			sortMap[sorter.order] || 'desc',
			filters.type?.[0] as ActivityType,
			current,
			filters.causer?.[0],
			filters.causer_type?.[0] as CauserType
		)

		setIsLoading(false)
		setActivities(data)
		setPagination({ ...pagination, total, pageSize: per_page })
	}

	useEffect(() => {
		const initActivities = async (): Promise<void> => {
			setIsLoading(true)
			const [activityData, userData] = await Promise.all([activityService.search('desc'), userService.getAll()])
			setIsLoading(false)

			setActivities(activityData.data)
			setUsers(userData.data)
			setPagination({
				...pagination,
				total: activityData.total,
				pageSize: activityData.per_page
			})
		}
		initActivities()
	}, [])

	return (
		<Table<Activity>
			columns={columns}
			expandedRowRender={expandedRowRender}
			pagination={pagination}
			dataSource={activities}
			loading={isLoading}
			onChange={handleTableChange}
			size={'small'}
		/>
	)
}

export default ActivityPage
